// Builds the presence join-token provider (BL-410).
//
// SPLIT DELIBERATELY. The parts that are identical across apps live here — which
// topics can be minted for, and the never-throw / null-on-anything-unusable
// contract. The actual request stays with each app, because each has its own
// typed API client carrying its own registry, auth-redirect behaviour, and
// credentials policy. That is `sharing.md` § 1's thin-wrapper shape: shared
// logic here, the few lines that adapt an app's own context there.
//
// It also keeps the DTO flowing end-to-end (ADR 027 / rule #35): the app's
// `mint` goes through `api.presence.token`, so the response type is inferred
// from the one registry declaration rather than re-asserted here.
//
// CLIENT-SAFE: no node crypto, no secret. This side only carries the token.

import type { PresenceTokenDto } from '../../schemas/responses/presence-token';

/**
 * Topics this provider can mint for: `auction:{id}:presence`.
 *
 * Anchored and matched rather than split, so a differently-shaped topic returns
 * `null` instead of producing a request for a garbage id.
 */
const AUCTION_PRESENCE_TOPIC = /^auction:([^:]+):presence$/;

/** Options for {@link createPresenceTokenFetcher}. */
export interface PresenceTokenFetcherOptions {
    /**
     * Ask this app's API for a token for one auction.
     *
     * Supplied per app because the call goes through that app's typed client.
     * Return `null` for any response that is not a usable token; throwing is
     * also tolerated (it is caught) but returning `null` is clearer.
     */
    mint: (auctionId: string) => Promise<PresenceTokenDto | null>;
}

/**
 * Build the join-token provider the realtime subscriber calls before joining a
 * presence topic.
 *
 * RETURNS `null` RATHER THAN THROWING for every failure, and that is the
 * contract: the commonest "failure" is a 403 for a bidder who is not approved on
 * this auction, which is a perfectly ordinary state and not an error. The
 * subscriber treats `null` as "do not join", so an unentitled client simply has
 * no presence rather than a broken page.
 *
 * @param options - The app's mint call.
 * @returns A provider suitable for the subscriber's `getJoinToken`.
 * @sideEffect Calls `mint`, which issues a request.
 */
export function createPresenceTokenFetcher({
    mint,
}: PresenceTokenFetcherOptions): (topic: string) => Promise<string | null> {
    return async (topic: string): Promise<string | null> => {
        if (!AUCTION_PRESENCE_TOPIC.test(topic)) return null;
        const auctionId = AUCTION_PRESENCE_TOPIC.exec(topic)?.[1];
        if (!auctionId) return null;

        try {
            const minted = await mint(auctionId);
            const token = minted?.token;
            return typeof token === 'string' && token.length > 0 ? token : null;
        } catch {
            // Must not propagate: the subscriber calls this inside a join, and an
            // unhandled rejection there would surface as a broken page for a blip
            // the next reconnect fixes by itself.
            return null;
        }
    };
}
